import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { 
  MapPin, 
  ShoppingCart, 
  Phone, 
  ChevronLeft
} from 'lucide-react';
import { servicesCardData as defaultServicesCardData } from '../utils/servicesCardData';
import ServicesSidebar from '../components/ServicesSidebar';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import '../styles/Services.css';
import '../styles/ServiceDetail.css';
import '../styles/ServicesProfile.css';

// Default contact phone number for booking
const DEFAULT_BOOKING_PHONE = '+1 (555) 123-4567'; // Change this to your actual booking phone number

// Services Profile View Component
const ServicesProfileView = ({ profile, onClose, onAddToCart, onContact, onImageClick }) => {
  // Prepare portfolio items with spans for masonry layout
  const portfolioItems = profile.portfolio?.map((url, idx) => {
    let span = '';
    if (idx === 0) span = 'row-span-2';
    else if (idx === 3) span = 'col-span-2';
    else span = 'row-span-1';
    return { url, span };
  }) || [];

  return (
    <div className="services-profile-wrapper">
      {/* Background Gradient Orbs */}
      <div className="services-profile-bg-orbs">
        <div className="services-profile-orb services-profile-orb-purple"></div>
        <div className="services-profile-orb services-profile-orb-indigo"></div>
      </div>
      
      <div className="services-profile-container">
        {/* Header Navigation */}
        <div className="services-profile-header-nav">
          <button className="services-profile-back-btn" onClick={onClose}>
            <div className="services-profile-back-icon">
              <ChevronLeft size={20} />
            </div>
            <span className="services-profile-back-text">Back to Profiles</span>
          </button>
        </div>

        <div className="services-profile-grid">
          {/* LEFT SIDEBAR - PROFILE STICKY */}
          <div className="services-profile-sidebar">
            <div className="services-profile-sticky">
              {/* Profile Card */}
              <div className="services-profile-card">
                <div className="services-profile-card-content">
                  <div className="services-profile-image-wrapper">
                    <img 
                      src={profile.image} 
                      alt={profile.name} 
                      className="services-profile-main-image"
                    />
                    <div className="services-profile-image-overlay"></div>
                  </div>
                  
                  <h1 className="services-profile-name">{profile.name}</h1>
                  <p className="services-profile-role">{profile.title}</p>
                  
                  <div className="services-profile-meta-info">
                    <div className="services-profile-meta-item">
                      <MapPin size={16} className="services-profile-meta-icon" />
                      <span>{profile.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT - SCROLLABLE */}
          <div className="services-profile-content">
            {/* About Section */}
            <section className="services-profile-section">
              <div className="services-profile-section-header">
                <div className="services-profile-divider"></div>
                <h2 className="services-profile-section-title">About the Artist</h2>
                <div className="services-profile-divider"></div>
              </div>
              <p className="services-profile-about-text">
                <span className="services-profile-drop-cap"></span>
                {profile.bio} Whether it's on stage or in front of the camera, {profile.name.split(' ')[0]} brings a raw intensity that captivates audiences. His dedication to craft is matched only by his professional demeanor on set.
              </p>
            </section>

            {/* Specialties */}
            <section className="services-profile-section">
              <h3 className="services-profile-skills-title">Expertise & Skills</h3>
              <div className="services-profile-specialties">
                {profile.specialties?.map((tag, index) => (
                  <span 
                    key={index} 
                    className="services-profile-specialty-tag"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </section>

            {/* Portfolio Grid - Masonry Style */}
            <section className="services-profile-section">
              <div className="services-profile-portfolio-header">
                <h2 className="services-profile-portfolio-title">Portfolio</h2>
              </div>
              
              <div className="services-profile-portfolio-grid">
                {portfolioItems.map((item, idx) => {
                  const style = {};
                  if (item.span === 'row-span-2') {
                    style.gridRow = 'span 2';
                  } else if (item.span === 'col-span-2') {
                    style.gridColumn = 'span 2';
                  }
                  return (
                    <div 
                      key={idx} 
                      className="services-profile-portfolio-item"
                      style={style}
                      onClick={() => onImageClick(item.url)}
                    >
                      <img 
                        src={item.url} 
                        alt={`Portfolio ${idx + 1}`} 
                        className="services-profile-portfolio-image"
                      />
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Bottom Action Bar */}
            <div className="services-profile-action-bar">
              <div className="services-profile-action-bar-content">
                <button 
                  className="services-profile-book-btn"
                  onClick={onContact}
                >
                  <Phone size={20} className="services-profile-btn-icon" />
                  Book Now
                </button>
                <button 
                  className="services-profile-cart-btn"
                  onClick={onAddToCart}
                >
                  <ShoppingCart size={20} className="services-profile-btn-icon" />
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Get merged services data (default + admin additions, excluding deleted)
const getMergedServicesData = () => {
  const savedServices = localStorage.getItem('adminServicesData');
  const deletedArtists = JSON.parse(localStorage.getItem('deletedArtists') || '{}');
  let mergedData = {};
  
  // Start with default data - deep copy and filter out deleted artists
  Object.keys(defaultServicesCardData).forEach(category => {
    const deletedIds = new Set(deletedArtists[category] || []);
    mergedData[category] = (defaultServicesCardData[category] || []).filter(
      artist => !deletedIds.has(artist.id)
    );
  });
  
  // Merge admin-added artists (also filter out deleted ones)
  if (savedServices) {
    try {
      const parsed = JSON.parse(savedServices);
      Object.keys(parsed).forEach(category => {
        const deletedIds = new Set(deletedArtists[category] || []);
        if (mergedData[category]) {
          // Merge arrays: add admin artists to default artists (avoid duplicates and deleted)
          const existingIds = new Set(mergedData[category].map(a => a.id));
          const adminArtists = (parsed[category] || []).filter(
            a => !existingIds.has(a.id) && !deletedIds.has(a.id)
          );
          mergedData[category] = [...mergedData[category], ...adminArtists];
        } else {
          // New category entirely from admin (filter deleted)
          mergedData[category] = (parsed[category] || []).filter(
            a => !deletedIds.has(a.id)
          );
        }
      });
    } catch (error) {
      console.error('Error loading services data:', error);
    }
  }
  
  return mergedData;
};

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all-services');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [servicesCardData, setServicesCardData] = useState(getMergedServicesData());
  const { addToCart } = useCart();


  // Update services data when localStorage changes (listen for storage events)
  useEffect(() => {
    const handleStorageChange = () => {
      setServicesCardData(getMergedServicesData());
    };

    // Listen for cross-tab/window changes
    window.addEventListener('storage', handleStorageChange);
    // Listen for same-window updates (custom event from admin page)
    window.addEventListener('adminServicesUpdated', handleStorageChange);
    // Also check on focus in case data changed in another tab
    window.addEventListener('focus', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminServicesUpdated', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, []);

  // Close profile view when category changes
  useEffect(() => {
    setSelectedProfile(null);
    setSelectedImage(null);
  }, [selectedCategory]);

  // Get all service categories
  const serviceCategories = Object.keys(servicesCardData);

  // Filter categories based on search
  const filteredCategories = serviceCategories.filter(category => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return category.toLowerCase().includes(searchLower);
  });

  // Get profiles to display - get all profiles from selected category or all categories
  const getProfilesToDisplay = () => {
    if (!selectedCategory) return [];
    
    // If "All Services" is selected, get all professionals from all categories
    if (selectedCategory === 'all-services') {
      const allProfiles = [];
      Object.keys(servicesCardData).forEach(category => {
        const categoryProfiles = servicesCardData[category] || [];
        allProfiles.push(...categoryProfiles);
      });
      
      // Filter by search if needed
      if (!searchQuery) return allProfiles;
      const searchLower = searchQuery.toLowerCase();
      return allProfiles.filter(profile => 
        profile.name.toLowerCase().includes(searchLower) ||
        profile.title.toLowerCase().includes(searchLower) ||
        profile.bio.toLowerCase().includes(searchLower) ||
        profile.specialties.some(s => s.toLowerCase().includes(searchLower))
      );
    }
    
    // Otherwise, get profiles from selected category
    const categoryProfiles = servicesCardData[selectedCategory] || [];
    // Filter by search if needed
    if (!searchQuery) return categoryProfiles;
    const searchLower = searchQuery.toLowerCase();
    return categoryProfiles.filter(profile => 
      profile.name.toLowerCase().includes(searchLower) ||
      profile.title.toLowerCase().includes(searchLower) ||
      profile.bio.toLowerCase().includes(searchLower) ||
      profile.specialties.some(s => s.toLowerCase().includes(searchLower))
    );
  };

  const profilesToDisplay = getProfilesToDisplay();

  // Handle profile selection
  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    setSelectedImage(null);
  };

  // Handle contact for booking - redirects to default phone number
  const handleContactForBooking = () => {
    window.location.href = `tel:${DEFAULT_BOOKING_PHONE}`;
  };

  // Handle add to cart
  const handleAddToCart = (profile) => {
    // Convert profile to cart item format
    const cartItem = {
      id: profile.id,
      name: profile.name,
      title: profile.title,
      image: profile.image,
      category: selectedCategory === 'all-services' ? 'Service' : selectedCategory,
      price: 0, // Services might not have a price, or you can add pricing later
      type: 'service'
    };
    addToCart(cartItem);
  };

  // Handle image click
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const closeProfileModal = () => {
    setSelectedProfile(null);
    setSelectedImage(null);
  };

  return (
    <>
      <SEO 
        title="Services - Professional Photography & Videography Services | BookMyCamera"
        description="Professional photography and videography services in Bangalore. Wedding photography, event coverage, corporate shoots, and specialized photography services."
        keywords="photography services bangalore, videography services, wedding photography services, event photography, corporate photography bangalore"
        canonicalUrl="https://bookmycamera.com/services"
      />
      <div className="services-page-wrapper">
      {/* Sidebar Container - Separate */}
      <div className="services-sidebar-container">
        <ServicesSidebar
          categories={filteredCategories}
          selectedCategory={selectedCategory}
          onCategorySelect={setSelectedCategory}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          totalCount={filteredCategories.length}
          getCategoryCount={(category) => servicesCardData[category]?.length || 0}
          servicesCardData={servicesCardData}
          onProfileSelect={handleProfileSelect}
        />
      </div>

      {/* Services Container - Separate */}
      <div className="services-container">
        <div className="services-header">
          <h1>Our Services</h1>
          <p>Professional services for your production needs</p>
        </div>

        <div className="services-layout">
          <main className="services-main">
            {selectedProfile ? (
              // New Detailed Profile View
              <ServicesProfileView 
                profile={selectedProfile}
                onClose={closeProfileModal}
                onAddToCart={() => handleAddToCart(selectedProfile)}
                onContact={handleContactForBooking}
                onImageClick={handleImageClick}
              />
            ) : profilesToDisplay.length === 0 ? (
              <div className="services-empty">
                <h3>{selectedCategory ? 'No professionals found' : 'Please select a category'}</h3>
                <p>{selectedCategory === 'all-services' ? 'No professionals found matching your search' : selectedCategory ? 'Try adjusting your search or filter' : 'Choose a service category from the sidebar to view available professionals'}</p>
              </div>
            ) : (
              // Profile Cards Grid
              <div className="profile-cards-grid-view">
                <div className="profile-cards-grid">
                  {profilesToDisplay.map((profile, index) => (
                    <div
                      key={profile.id}
                      className="profile-card-preview card-animate"
                      style={{ 
                        animationDelay: `${index * 0.08}s`,
                        '--card-index': index
                      }}
                      onClick={() => handleProfileSelect(profile)}
                    >
                      <div className="card-preview-image-container">
                        <img src={profile.image} alt={profile.name} />
                      </div>
                      <div className="card-preview-content">
                        <h3>{profile.name}</h3>
                        <p className="card-preview-title">{profile.title}</p>
                        <div className="card-preview-meta">
                          <span><FaMapMarkerAlt /> {profile.location}</span>
                          <span>{profile.experience}</span>
                        </div>
                        <p className="card-preview-bio">{profile.bio.substring(0, 100)}...</p>
                        <div className="card-preview-specialties">
                          {profile.specialties.slice(0, 3).map((specialty, index) => (
                            <span key={index} className="card-preview-specialty">{specialty}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={closeImageModal}>
          <span className="modal-close">&times;</span>
          <img src={selectedImage} alt="Portfolio" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
    </>
  );
};

export default Services;

